import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { EChartsCoreOption } from 'echarts/core';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { TranslationService } from 'src/app/shared/services/translation.service';
import { ReservationQueriesService } from 'src/app/shared/services/reservation-queries.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

echarts.use([LineChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer]);

@Component({
  selector: 'app-reservation-history-chart',
  standalone: true,
  imports: [NgxEchartsDirective, TranslatePipe, MatProgressSpinner],
  providers: [provideEchartsCore({ echarts })],
  templateUrl: './reservation-history-chart.component.html',
  styleUrl: './reservation-history-chart.component.scss',
})
export class ReservationHistoryChartComponent implements OnChanges {
  @Input() monthNames: { [key: number]: string } = {};
  @Input() selectedYear: number = new Date().getFullYear();
  @Input() only_recurrings: boolean | undefined;
  @Input() externalData: { [month: string]: number } | undefined;

  chartOptions: EChartsCoreOption = {};
  loading = true;
  hasData = false;

  constructor(
    protected translationService: TranslationService,
    private queriesService: ReservationQueriesService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedYear'] || changes['monthNames'] || changes['externalData']) {
      if (this.externalData) {
        this.buildFromExternal();
      } else {
        this.loadAllMonths();
      }
    }
  }

  private buildFromExternal(): void {
    const months: string[] = [];
    const totalData: number[] = [];

    for (let i = 1; i <= 12; i++) {
      const monthName = this.monthNames[i];
      if (!monthName) continue;

      const shortLabel = this.translationService.translateDays(monthName, 'MONTHS_SINGLE').substring(0, 3);
      months.push(shortLabel);
      totalData.push(this.externalData![monthName] || 0);
    }

    this.hasData = totalData.some((v) => v > 0);
    this.buildChart(months, totalData, [], []);
    this.loading = false;
  }

  private async loadAllMonths(): Promise<void> {
    this.loading = true;
    this.hasData = false;

    const totalData: number[] = [];
    const bookingsData: number[] = [];
    const recurringData: number[] = [];
    const months: string[] = [];

    const promises = Array.from({ length: 12 }, (_, i) =>
      this.queriesService.getTotalReservationsMadeFromCurrentUserByMonth(i + 1, this.selectedYear)
    );

    const results = await Promise.all(promises);

    for (let i = 0; i < 12; i++) {
      const monthName = this.monthNames[i + 1];
      if (!monthName) continue;

      const shortLabel = this.translationService.translateDays(monthName, 'MONTHS_SINGLE').substring(0, 3);
      months.push(shortLabel);

      const { data, count } = results[i];
      const total = count || 0;
      const recurring = data ? data.filter((r: any) => r.bookedBy == null).length : 0;
      const bookings = total - recurring;

      totalData.push(total);
      bookingsData.push(bookings);
      recurringData.push(recurring);
    }

    this.hasData = totalData.some((v) => v > 0);
    this.buildChart(months, totalData, bookingsData, recurringData);
    this.loading = false;
  }

  private buildChart(months: string[], totalData: number[], bookingsData: number[], recurringData: number[]): void {
    const series: any[] = [
      {
        name: this.translationService.translate('Total'),
        type: 'line',
        data: totalData,
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: { width: 2, color: '#2ecc71' },
        itemStyle: { color: '#2ecc71' },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(46, 204, 113, 0.15)' },
              { offset: 1, color: 'rgba(46, 204, 113, 0)' },
            ],
          },
        },
      },
    ];

    if (!this.only_recurrings && bookingsData.length > 0) {
      series.push({
        name: this.translationService.translate('Bookings'),
        type: 'line',
        data: bookingsData,
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: { width: 2, color: '#3b82f6' },
        itemStyle: { color: '#3b82f6' },
      });
    }

    if (recurringData.length > 0) {
      series.push({
        name: this.translationService.translate('Recurring'),
        type: 'line',
        data: recurringData,
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: { width: 2, color: '#f59e0b' },
        itemStyle: { color: '#f59e0b' },
      });
    }

    this.chartOptions = {
      tooltip: {
        trigger: 'axis',
        backgroundColor: '#ffffff',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        textStyle: { color: '#374151', fontSize: 12 },
      },
      legend: {
        bottom: 0,
        itemWidth: 12,
        itemHeight: 12,
        textStyle: { color: '#6b7280', fontSize: 11 },
      },
      grid: {
        top: 16,
        right: 16,
        bottom: 40,
        left: 36,
        containLabel: false,
      },
      xAxis: {
        type: 'category',
        data: months,
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisTick: { show: false },
        axisLabel: { color: '#9ca3af', fontSize: 11 },
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { color: '#f3f4f6' } },
        axisLabel: { color: '#9ca3af', fontSize: 11 },
      },
      series,
    };
  }
}
